import React, { useState } from 'react';
import { Building2, DollarSign, Phone, Mail, MapPin, Clock, Send, Globe } from 'lucide-react';
import toast from 'react-hot-toast';

interface LotOwnerSignupForm {
  businessName: string;
  contactName: string;
  email: string;
  phone: string;
  lotName: string;
  lotSlug: string;
  address: string;
  description: string;
  hourlyRate: number;
  maxDailyRate: number;
  maxHours: number;
}

export default function LotOwnerSignup() {
  const [formData, setFormData] = useState<LotOwnerSignupForm>({
    businessName: '',
    contactName: '',
    email: '',
    phone: '',
    lotName: '',
    lotSlug: '',
    address: '',
    description: '',
    hourlyRate: 4.50,
    maxDailyRate: 18.00,
    maxHours: 12,
  });

  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Validate slug format
      if (formData.lotSlug && !/^[a-z0-9\-]+$/.test(formData.lotSlug)) {
        toast.error('URL slug can only contain lowercase letters, numbers, and hyphens');
        return;
      }

      // Use conditional API endpoint for localhost vs production
      const apiUrl = window.location.hostname === 'localhost' 
        ? '/api/onboard-lot-owner'
        : '/.netlify/functions/onboard-lot-owner';
      
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.details || 'Failed to create lot');
      }

      const result = await response.json();
      
      toast.success('Lot created successfully! Redirecting to Stripe onboarding...');
      
      // Redirect to Stripe onboarding
      setTimeout(() => {
        window.location.href = result.onboardingUrl;
      }, 2000);

    } catch (error) {
      console.error('Signup error:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to create lot');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'number' ? parseFloat(value) || 0 : value,
    }));
  };

  // Track if user has manually edited the slug
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);

  // Auto-generate slug from lot name
  const handleLotNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const lotName = e.target.value;
    const autoSlug = lotName
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-+|-+$/g, '') // Remove leading/trailing dashes
      .trim();

    setFormData(prev => ({
      ...prev,
      lotName,
      // Only auto-update slug if user hasn't manually edited it
      lotSlug: slugManuallyEdited ? prev.lotSlug : autoSlug
    }));
  };

  // Handle manual slug editing
  const handleLotSlugChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSlugManuallyEdited(true);
    setFormData(prev => ({
      ...prev,
      lotSlug: value
    }));
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl mx-auto">
        <div className="bg-white shadow-xl rounded-lg overflow-hidden">
          <div className="px-6 py-8 bg-blue-600 text-white">
            <div className="flex items-center">
              <Building2 className="h-8 w-8 mr-3" />
              <div>
                <h1 className="text-3xl font-bold">Join Matrix Lot</h1>
                <p className="text-blue-100 mt-2">Set up your parking lot in minutes</p>
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="px-6 py-8 space-y-8">
            {/* Business Information */}
            <div>
              <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                <Building2 className="h-5 w-5 mr-2" />
                Business Information
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Business Name *
                  </label>
                  <input
                    type="text"
                    name="businessName"
                    required
                    value={formData.businessName}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="ABC Parking Solutions"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Contact Name *
                  </label>
                  <input
                    type="text"
                    name="contactName"
                    required
                    value={formData.contactName}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="John Smith"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <Mail className="inline h-4 w-4 mr-1" />
                    Email Address *
                  </label>
                  <input
                    type="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="john@abcparking.com"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <Phone className="inline h-4 w-4 mr-1" />
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    required
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="+1 (555) 123-4567"
                  />
                </div>
              </div>
            </div>

            {/* Lot Details */}
            <div>
              <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                <MapPin className="h-5 w-5 mr-2" />
                Lot Details
              </h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Lot Name *
                  </label>
                  <input
                    type="text"
                    name="lotName"
                    required
                    value={formData.lotName}
                    onChange={handleLotNameChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Downtown Parking Lot"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <Globe className="inline h-4 w-4 mr-1" />
                    URL Slug *
                  </label>
                  <div className="flex">
                    <span className="inline-flex items-center px-3 rounded-l-lg border border-r-0 border-gray-300 bg-gray-50 text-gray-500 text-sm">
                      parkinglittleitaly.com/lots/
                    </span>
                    <input
                      type="text"
                      name="lotSlug"
                      required
                      value={formData.lotSlug}
                      onChange={handleLotSlugChange}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-r-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="downtown-parking"
                      pattern="^[a-z0-9\-]+$"
                      title="Only lowercase letters, numbers, and hyphens allowed"
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    This will be your lot's public URL. Only lowercase letters, numbers, and hyphens.
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Address *
                  </label>
                  <input
                    type="text"
                    name="address"
                    required
                    value={formData.address}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="123 Main St, Toronto, ON M5V 3A8"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Convenient downtown parking with easy access to shopping and transit..."
                  />
                </div>
              </div>
            </div>

            {/* Pricing */}
            <div>
              <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                <DollarSign className="h-5 w-5 mr-2" />
                Pricing Settings
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Hourly Rate (CAD) *
                  </label>
                  <input
                    type="number"
                    name="hourlyRate"
                    required
                    step="0.25"
                    min="0"
                    value={formData.hourlyRate}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Max Daily Rate (CAD) *
                  </label>
                  <input
                    type="number"
                    name="maxDailyRate"
                    required
                    step="0.25"
                    min="0"
                    value={formData.maxDailyRate}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <Clock className="inline h-4 w-4 mr-1" />
                    Max Hours *
                  </label>
                  <input
                    type="number"
                    name="maxHours"
                    required
                    min="1"
                    max="24"
                    value={formData.maxHours}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <div className="pt-6">
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-blue-600 text-white py-3 px-6 rounded-lg font-medium hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              >
                {loading ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                ) : (
                  <Send className="h-5 w-5 mr-2" />
                )}
                {loading ? 'Creating Your Lot...' : 'Create Lot & Setup Payments'}
              </button>
              
              <p className="text-sm text-gray-600 text-center mt-4">
                After clicking submit, you'll be redirected to Stripe to set up payment processing.
                Matrix Lot charges a <strong>10% platform fee</strong> on all parking sessions.
              </p>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
} 